<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSignUp } from '@/composables/useSignUp'
import { toast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

const router = useRouter()
const { signUpWithEmail, signUpWithGoogle, isLoading, error, clearError } = useSignUp()

const signUpSuccess = ref(false)
const showGoogleDialog = ref(false)

// Email Sign-Up Form Schema
const signUpSchema = toTypedSchema(
  z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    displayName: z.string().min(1, 'Display name is required'),
    organizationName: z.string().min(1, 'Organization name is required'),
    projectName: z.string().min(1, 'Project name is required')
  })
)

// Handle Email Sign-Up
const handleEmailSignUp = async (values: any) => {
  clearError()

  const result = await signUpWithEmail({
    email: values.email,
    password: values.password,
    displayName: values.displayName,
    organizationName: values.organizationName,
    projectName: values.projectName
  })

  if (result) {
    signUpSuccess.value = true
    toast({
      title: 'Account created!',
      description: 'Please check your email to verify your address.'
    })
  } else if (error.value) {
    toast({
      title: 'Sign-up failed',
      description: error.value,
      variant: 'destructive'
    })
  }
}

// Google Sign-Up Form Schema
const googleSignUpSchema = toTypedSchema(
  z.object({
    displayName: z.string().min(1, 'Display name is required'),
    organizationName: z.string().min(1, 'Organization name is required'),
    projectName: z.string().min(1, 'Project name is required')
  })
)

// Handle Google Sign-Up Dialog Submit
const handleGoogleSignUpSubmit = async (values: any) => {
  clearError()

  const result = await signUpWithGoogle({
    displayName: values.displayName,
    organizationName: values.organizationName,
    projectName: values.projectName
  })

  if (result) {
    showGoogleDialog.value = false
    toast({
      title: 'Account created successfully',
      description: 'Welcome to Virunga!'
    })
    // Google sign-up returns custom_token, so navigate to sign in
    router.push('/auth')
  } else if (error.value) {
    toast({
      title: 'Google sign-up failed',
      description: error.value,
      variant: 'destructive'
    })
  }
}

const goToSignIn = () => {
  router.push('/auth')
}
</script>

<template>
  <!-- Full screen layout, grid only on large screens - matches AuthPage -->
  <div class="h-screen w-screen bg-white lg:grid lg:grid-cols-[1fr_auto]">
    <!-- Left Side - Image (only visible on large screens) -->
    <div class="hidden lg:flex bg-gradient-to-br from-blue-50 to-indigo-50 items-center justify-center p-16">
      <img
        src="@/assets/img/earth-art.png"
        alt="Earth"
        class="w-full h-auto object-contain"
      />
    </div>

    <!-- Right Side - Sign Up Form -->
    <div class="h-screen w-screen lg:w-[500px] flex flex-col items-center justify-center px-8 bg-white">
      <div class="w-full max-w-sm space-y-6">
        <!-- Header -->
        <div class="text-center space-y-2">
          <h1 class="text-3xl font-bold tracking-tight text-gray-900">
            Create Your Account
          </h1>
          <p class="text-sm text-gray-600">
            Sign up for a free account
          </p>
        </div>

        <!-- Error Alert -->
        <Alert v-if="error" variant="destructive">
          <AlertDescription>{{ error }}</AlertDescription>
        </Alert>

        <!-- Success View -->
        <div v-if="signUpSuccess" class="space-y-4">
          <Alert>
            <AlertDescription>
              Account created successfully! Please check your email to verify your address before signing in.
            </AlertDescription>
          </Alert>

          <Button class="w-full" @click="goToSignIn">
            Go to Sign In
          </Button>
        </div>

        <!-- Sign-Up Form -->
        <div v-else class="space-y-4">
          <!-- Google Sign-Up Button with Dialog -->
          <Dialog v-model:open="showGoogleDialog">
            <DialogTrigger as-child>
              <Button
                variant="outline"
                class="w-full gap-3"
                :disabled="isLoading"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </DialogTrigger>
            <DialogContent class="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Complete Your Profile</DialogTitle>
                <DialogDescription>
                  Please provide a few details to set up your account
                </DialogDescription>
              </DialogHeader>

              <!-- Google Sign-Up Form -->
              <Form
                v-slot="{ meta }"
                :validation-schema="googleSignUpSchema"
                :initial-values="{ displayName: '', organizationName: '', projectName: '' }"
                @submit="handleGoogleSignUpSubmit"
              >
                <div class="space-y-4">
                  <FormField v-slot="{ componentField }" name="displayName">
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          v-bind="componentField"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField v-slot="{ componentField }" name="organizationName">
                    <FormItem>
                      <FormLabel>Organization Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Acme Corp"
                          v-bind="componentField"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField v-slot="{ componentField }" name="projectName">
                    <FormItem>
                      <FormLabel>Project Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="My First Project"
                          v-bind="componentField"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <Button type="submit" class="w-full" :disabled="isLoading || !meta.valid">
                    {{ isLoading ? 'Creating Account...' : 'Continue' }}
                  </Button>
                </div>
              </Form>
            </DialogContent>
          </Dialog>

          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <span class="w-full border-t" />
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <!-- Email Sign-Up Form -->
          <Form
            v-slot="{ meta }"
            :validation-schema="signUpSchema"
            :initial-values="{ email: '', password: '', displayName: '', organizationName: '', projectName: '' }"
            @submit="handleEmailSignUp"
          >
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" name="email">
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="password">
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="At least 6 characters"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="displayName">
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John Doe"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="organizationName">
                <FormItem>
                  <FormLabel>Organization Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Acme Corp"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="projectName">
                <FormItem>
                  <FormLabel>Project Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="My First Project"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <Button type="submit" class="w-full" :disabled="isLoading || !meta.valid">
                {{ isLoading ? 'Creating Account...' : 'Create Account' }}
              </Button>
            </div>
          </Form>

          <!-- Sign In Link -->
          <div class="text-center text-sm pt-4 border-t">
            <span class="text-gray-600">Already have an account? </span>
            <Button variant="link" class="p-0 h-auto" @click="goToSignIn">
              Sign in
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
